<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { beginWait, registerRetry } from '../helpers/pageLoad';

/**
 * What a page shows while its data is on its way — never a spinner in the
 * middle of the screen (prototype "WAITING, WITHOUT A SPINNER").
 *
 * Nothing for the first 200ms, so a fast answer shows no loading state at
 * all. Then skeletons, shaped like what is coming so nothing moves when it
 * lands: `variant` says which shape. Its being here is also what keeps the
 * progress line running; its going is not a second arrival — the page faded
 * in once, with its route, skeletons and all (pageLoad). After five seconds the shell says the connection looks slow, with
 * Retry (XlSlowNotice), which asks through `onRetry` where the page gives one.
 *
 * Only the content area: the shell around it was drawn at once.
 *
 * Actions inside a page (save, upload, send) are not this — they show an
 * inline spinner on their own button (XlButton `loading`).
 */
const props = defineProps({
  /**
   * rows    a card of list rows — icon, two lines, a pill (the default)
   * table   a header and rows of cells
   * cards   a grid of cards
   * detail  a wide block and the lines under it
   * form    labelled fields
   * lines   a few lines, for a small panel or widget
   */
  variant: { type: String, default: 'rows' },
  rows: { type: Number, default: 4 },
  // A page title and its lead above the rows, for a page whose heading is its own.
  heading: { type: Boolean, default: false },
  // How the page asks again. Without one, Retry mounts the page afresh.
  onRetry: { type: Function, default: null },
  delay: { type: Number, default: 200 },
  // Kept so existing callers keep working; a skeleton has no size or colour.
  size: { type: String, default: null },
  color: { type: String, default: null },
});

const visible = ref(false);

let endWait = null;
let withdrawRetry = null;
let showTimer = null;

onMounted(() => {
  endWait = beginWait();
  if (props.onRetry) withdrawRetry = registerRetry(props.onRetry);
  showTimer = setTimeout(() => { visible.value = true; }, props.delay);
});

onBeforeUnmount(() => {
  clearTimeout(showTimer);
  withdrawRetry?.();
  endWait?.();
});

// Varied widths, so a block of rows does not read as a solid slab. Fixed per
// row rather than random, so the skeleton does not change on every render.
const WIDTHS = [[46, 58], [38, 64], [52, 49], [41, 61], [56, 52], [35, 57], [49, 63], [44, 55]];
const rowWidths = computed(() => Array.from({ length: props.rows }, (_, i) => WIDTHS[i % WIDTHS.length]));
</script>

<template>
  <div class="xl-loading" :class="{ 'xl-loading--held': !visible }" data-testid="page-loading" :data-variant="props.variant" aria-busy="true">
    <div aria-hidden="true">
      <template v-if="props.heading">
        <span class="sk sk--title" style="width: 210px" />
        <span class="sk sk--lead" style="width: 330px" />
      </template>

      <!-- rows: a card of list rows (prototype skRows) -->
      <div v-if="props.variant === 'rows'" class="xl-loading__card">
        <div v-for="(w, i) in rowWidths" :key="i" class="xl-loading__row">
          <span class="sk sk--icon" />
          <div class="xl-loading__text">
            <span class="sk" :style="{ width: `${w[0]}%` }" />
            <span class="sk sk--small" :style="{ width: `${w[1]}%` }" />
          </div>
          <span class="sk sk--pill" />
        </div>
      </div>

      <!-- table: a header row and rows of cells -->
      <div v-else-if="props.variant === 'table'" class="xl-loading__card">
        <div class="xl-loading__row xl-loading__row--head">
          <span class="sk sk--small" style="width: 28%" />
          <span class="sk sk--small" style="width: 12%" />
          <span class="sk sk--small" style="width: 12%" />
          <span class="sk sk--small" style="width: 14%" />
        </div>
        <div v-for="(w, i) in rowWidths" :key="i" class="xl-loading__row">
          <span class="sk" :style="{ width: `${w[0] - 12}%` }" />
          <span class="sk sk--small" style="width: 12%" />
          <span class="sk sk--small" style="width: 12%" />
          <span class="sk sk--chip" />
        </div>
      </div>

      <!-- cards: a grid -->
      <div v-else-if="props.variant === 'cards'" class="xl-loading__grid">
        <div v-for="(w, i) in rowWidths" :key="i" class="xl-loading__card xl-loading__card--tile">
          <span class="sk sk--block" />
          <span class="sk" :style="{ width: `${w[0] + 20}%` }" />
          <span class="sk sk--small" :style="{ width: `${w[1]}%` }" />
        </div>
      </div>

      <!-- detail: a wide block and the lines under it -->
      <div v-else-if="props.variant === 'detail'" class="xl-loading__card xl-loading__card--padded">
        <span class="sk sk--block sk--tall" />
        <span class="sk sk--title" style="width: 48%" />
        <span v-for="(w, i) in rowWidths.slice(0, 3)" :key="i" class="sk sk--small" :style="{ width: `${w[1] + 30}%` }" />
      </div>

      <!-- form: labelled fields -->
      <div v-else-if="props.variant === 'form'" class="xl-loading__card xl-loading__card--padded">
        <div v-for="(w, i) in rowWidths" :key="i" class="xl-loading__field">
          <span class="sk sk--small" :style="{ width: `${Math.round(w[0] / 2)}%` }" />
          <span class="sk sk--field" />
        </div>
      </div>

      <!-- lines: a small panel -->
      <div v-else class="xl-loading__lines">
        <span v-for="(w, i) in rowWidths" :key="i" class="sk" :style="{ width: `${w[0] + 30}%` }" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.xl-loading { width: 100%; font-family: var(--xl-font-family, inherit); }
/* The first 200ms: drawn in its place, not yet seen, so nothing moves when it shows. */
.xl-loading--held { visibility: hidden; }

/* Prototype `.sk`: a shimmering bar. */
@keyframes xl-loading-sheen { to { background-position: 200% 0; } }

.sk {
  display: block;
  flex: 0 0 auto;
  height: 13px;
  border-radius: 7px;
  background: linear-gradient(90deg, #F6F2F8 25%, #EDE7F0 37%, #F6F2F8 63%);
  background-size: 200% 100%;
  animation: xl-loading-sheen 1.4s ease-in-out infinite;
}

.sk--small { height: 11px; }
.sk--title { height: 22px; border-radius: 8px; margin: 0 0 8px; }
.sk--lead { height: 12px; margin: 0 0 24px; }
.sk--icon { width: 38px; height: 38px; border-radius: 11px; }
.sk--pill { width: 74px; height: 30px; border-radius: 99px; }
.sk--chip { width: 16%; height: 26px; border-radius: 99px; }
.sk--block { width: 100%; height: 120px; border-radius: 12px; }
.sk--tall { height: 160px; }
.sk--field { width: 100%; height: 40px; border-radius: 10px; }

/* Prototype `.skcard` / `.skrow`. */
.xl-loading__card {
  margin-bottom: 16px;
  padding: 4px 18px;
  border: 1px solid #EDE7F0;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(28, 23, 32, 0.04);
}

.xl-loading__card--padded,
.xl-loading__card--tile {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px;
}

.xl-loading__row {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 15px 0;
  border-bottom: 1px solid #F6F2F8;
}

.xl-loading__row:last-child { border-bottom: none; }
.xl-loading__row--head { padding: 12px 0; }

.xl-loading__text { flex: 1; display: flex; flex-direction: column; gap: 8px; min-width: 0; }

.xl-loading__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

.xl-loading__field { display: flex; flex-direction: column; gap: 8px; }
.xl-loading__lines { display: flex; flex-direction: column; gap: 10px; padding: 6px 0; }

/* The shimmer goes; the shapes stay, because they still say what is coming. */
@media (prefers-reduced-motion: reduce) {
  .sk { animation: none; }
}
</style>
